import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';
import type { Address } from '../../../../graphql/generated/graphql';
import { EMPTY_FORM, toFormState, type AddressFormState } from '../utils';

export interface UseAddressFormStateOptions {
  open: boolean;
  value: Address | null | undefined;
  /** Optional debounced notifier — fires after `debounceMs` of idle between
   * `setField` calls. Stays in sync with the latest closure without
   * resetting the timer. */
  onFieldChange?: (field: keyof AddressFormState, next: string) => void;
  /** Debounce window for `onFieldChange`. Defaults to 300ms. */
  debounceMs?: number;
}

export interface UseAddressFormStateResult {
  form: AddressFormState;
  setField: (field: keyof AddressFormState, next: string) => void;
  submitError: string | null;
  setSubmitError: (err: string | null) => void;
}

export function useAddressFormState({
  open,
  value,
  onFieldChange,
  debounceMs = 300
}: UseAddressFormStateOptions): UseAddressFormStateResult {
  const [form, setForm] = useState<AddressFormState>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Latest-callback ref so debounce isn't recreated when a new callback closure
  // is passed each render — recreating would reset the debounce timer.
  const onFieldChangeRef = useRef(onFieldChange);
  useEffect(() => {
    onFieldChangeRef.current = onFieldChange;
  });

  const debouncedNotify = useMemo(
    () =>
      debounce((field: keyof AddressFormState, next: string) => {
        onFieldChangeRef.current?.(field, next);
      }, debounceMs),
    [debounceMs]
  );

  useEffect(() => () => debouncedNotify.cancel(), [debouncedNotify]);

  useEffect(() => {
    if (open) {
      setForm(toFormState(value));
      setSubmitError(null);
      debouncedNotify.cancel();
    }
  }, [open, value, debouncedNotify]);

  const setField = useCallback(
    (field: keyof AddressFormState, next: string) => {
      setForm(prev => ({ ...prev, [field]: next }));
      debouncedNotify(field, next);
    },
    [debouncedNotify]
  );

  return {
    form,
    setField,
    submitError,
    setSubmitError
  };
}
